import {handleActions} from 'redux-actions';
import {List, Record, Map, fromJS} from 'immutable';
import moment from 'moment-timezone';

import generateUUID from 'web-app/util/uuid';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import * as SleepModalActions from 'web-app/react/modals-container/sleep/actions';
import * as DiaperModalActions from 'web-app/react/modals-container/diaper/actions';
import MealRegistrationActionTypes from 'web-app/react/entities/meals/food-consumption/action-types';
import * as SentryService from 'web-app/services/sentry';

import * as Actions from './actions';
import * as Util from './util';
import {DAYS_TO_CRAWL} from './constants';

export const Event = Record({
    eventId: null,
    behaviors: new BehaviorsCollection([]),
    type: String(),
    embed: Map(),
    from: null,
    to: null,
    title: String(),
    subtitle: String(),
    date: String(),
    childId: String(),
    deleting: false,
    isPast: false,
    originator: null,
    invitees: [],
});

const parseEmbed = (embed, event) => {
    if (typeof embed === 'object') {
        return fromJS(embed);
    } else {
        SentryService.captureMessage('Activity embed not an object', {
            extra: {
                event,
            },
        });
        return Map();
    }
};

const eventFromApi = (event, date, childId) => {
    let from = event.from ? moment(event.from) : null;
    from = from && event.embed.timezone ? from.tz(event.embed.timezone) : from;
    let to = event.to ? moment(event.to) : null;
    to = to && event.embed.timezone ? to.tz(event.embed.timezone) : to;
    return new Event({
        ...event,
        eventId: generateUUID(),
        type: Util.getEventType(event),
        behaviors: new BehaviorsCollection(event.behaviors),
        embed: parseEmbed(event.embed, event),
        from,
        to,
        date,
        isPast: moment.utc(date).isBefore(moment(), 'day'),
        childId,
    });
};

export const Day = Record({
    date: String(),
    unFormattedDate: String(),
    events: List(),
    loading: false,
});

export const dayFromApi = (day, childId) =>
    new Day({
        date: moment(day.day_localdate, 'YYYY-MM-DD').format('dddd, MMM D'),
        unFormattedDate: day.day_localdate,
        events: List(day.events).map(event => eventFromApi(event, day.day_localdate, childId)),
    });

const State = Record({
    loading: true,
    fetchingPast: false,
    fetchingFuture: false,
    days: List(),
    futureDate: moment().add(1, 'day'),
    // We subtract twice our initial limit for initial state, since we already fetch DAYS_TO_CRAWL days into the past on mount
    pastDate: moment().subtract(DAYS_TO_CRAWL * 2 * -1, 'days'),
    addActivitySpinner: false,
    futureCeilingReached: false,
    pastCeilingReached: false,
    stopInfiniteScroll: false,
    mapOfNewDays: Map(), // <date: string, boolean>
    childId: undefined,
});

const getEventPath = (state, event) => {
    const dayIndex = state.days.findIndex(day => day.date === moment(event.date).format('Do MMMM'));
    const eventIndex = state.days.get(dayIndex).events.findIndex(e => e.eventId === event.eventId);
    return [dayIndex, 'events', eventIndex];
};

const startSpinnerFunction = state => state.merge({addActivitySpinner: true});
const endSpinnerFunction = state => state.merge({addActivitySpinner: false});

export default handleActions(
    {
        [Actions.fetch.type]: (state, {payload}) =>
            new State({
                loading: true,
                childId: payload?.childId,
            }),
        [Actions.fetchSuccess.type]: (state, {payload}) =>
            payload?.childId === state.childId
                ? state.merge({
                      loading: false,
                      days: payload?.days,
                      pastDate: moment((payload as any).day),
                      pastCeilingReached: (payload as any).ceilingReached,
                      stopInfiniteScroll: (payload as any).shouldStopInfiniteScroll,
                      mapOfNewDays: Map((payload as any).days.map(day => [day.unFormattedDate, true])),
                  })
                : state.merge({
                      loading: false,
                  }),
        [Actions.fetchFailed.type]: state =>
            state.merge({
                loading: false,
            }),
        [Actions.fetchFuture.type]: state =>
            state.merge({
                fetchingFuture: true,
            }),
        [Actions.fetchFutureSuccess.type]: (state, {payload}) =>
            payload?.childId === state.childId
                ? state.merge({
                      fetchingFuture: false,
                      days: state.days.concat(payload?.days),
                      futureDate: moment((payload as any).day),
                      futureCeilingReached: (payload as any).ceilingReached,
                      mapOfNewDays: Map((payload as any).days.map(day => [day.unFormattedDate, true])),
                  })
                : state.merge({
                      fetchingFuture: false,
                  }),
        [Actions.fetchFutureFailed.type]: state =>
            state.merge({
                fetchingFuture: false,
                futureCeilingReached: true,
            }),
        [Actions.fetchPast.type]: state =>
            state.merge({
                fetchingPast: true,
            }),
        [Actions.fetchPastSuccess.type]: (state, {payload}) =>
            payload?.childId === state.childId
                ? state.merge({
                      fetchingPast: false,
                      days: state.days.concat(payload?.days),
                      pastDate: moment((payload as any).day),
                      pastCeilingReached: (payload as any).ceilingReached,
                      stopInfiniteScroll: (payload as any).shouldStopInfiniteScroll,
                      mapOfNewDays: Map((payload as any).days.map(day => [day.unFormattedDate, true])),
                  })
                : state.merge({
                      fetchingPast: false,
                  }),
        [Actions.fetchPastFailed.type]: state =>
            state.merge({
                fetchingPast: false,
                pastCeilingReached: true,
            }),
        [Actions.deleteEvent.type]: (state, {payload}) =>
            state.merge({
                days: state.days.updateIn(getEventPath(state, (payload as any).event), event =>
                    event
                        ? event.merge({
                              deleting: true,
                          })
                        : event,
                ),
            }),
        [Actions.deleteEventSuccess.type]: (state, {payload}) => {
            const [dayIndex, eventKey, eventIndex] = getEventPath(state, (payload as any).event);
            return state.merge({
                days:
                    state.days.getIn([dayIndex, eventKey]).size === 1
                        ? state.days.delete(dayIndex) // If one event, delete the whole day
                        : state.days.deleteIn([dayIndex, eventKey, eventIndex]), // Otherwise just delete the event
            });
        },
        [Actions.deleteEventFailed.type]: (state, {payload}) =>
            state.merge({
                days: state.days.updateIn(getEventPath(state, (payload as any).event), event =>
                    event
                        ? event.merge({
                              deleting: false,
                          })
                        : event,
                ),
            }),
        [Actions.refetchDays.type]: (state, {payload}) => {
            if (!(payload as any).startDate && !(payload as any).endDate) {
                return state.merge({
                    loading: true,
                });
            }
            return state.merge({
                days: state.days.map(day => {
                    const date = moment(day.unFormattedDate);
                    if (
                        (!(payload as any).startDate || date.isSameOrAfter((payload as any).startDate, 'days')) &&
                        (!(payload as any).endDate || date.isSameOrBefore((payload as any).endDate, 'days'))
                    ) {
                        return day.merge({
                            loading: true,
                        });
                    }
                    return day;
                }),
            });
        },
        [Actions.refetchDaysSuccess.type]: (state, {payload}) =>
            payload?.childId === state.childId
                ? state.merge({
                      loading: false,
                      days: state.days
                          .filterNot(day => (payload as any).days.some(d => d.unFormattedDate === day.unFormattedDate))
                          .concat(payload?.days),
                  })
                : state.merge({
                      loading: false,
                      days: state.days.map(day => day.set('loading', false)),
                  }),
        [Actions.refetchDaysFailed.type]: (state, {payload}) =>
            state.merge({
                loading: false,
                days: state.days.map(day => {
                    const date = moment(day.unFormattedDate);
                    if (
                        date.isSameOrAfter((payload as any).startDate, 'days') &&
                        date.isSameOrBefore((payload as any).endDate, 'days')
                    ) {
                        return day.merge({
                            loading: false,
                        });
                    }
                    return day;
                }),
            }),
        [SleepModalActions.nap.type]: startSpinnerFunction,
        [SleepModalActions.checkOn.type]: startSpinnerFunction,
        [MealRegistrationActionTypes.MEAL_REGISTRATIONS_CREATE]: startSpinnerFunction,
        [DiaperModalActions.diaper.type]: startSpinnerFunction,
        [Actions.checkIn.type]: startSpinnerFunction,
        [Actions.checkOut.type]: startSpinnerFunction,
        [SleepModalActions.napSuccess.type]: endSpinnerFunction,
        [SleepModalActions.napFailure.type]: endSpinnerFunction,
        [SleepModalActions.checkOnSuccess.type]: endSpinnerFunction,
        [SleepModalActions.checkOnFailure.type]: endSpinnerFunction,
        [MealRegistrationActionTypes.MEAL_REGISTRATIONS_CREATE_SUCCESS]: endSpinnerFunction,
        [MealRegistrationActionTypes.MEAL_REGISTRATIONS_CREATE_FAILED]: endSpinnerFunction,
        [DiaperModalActions.diaperSuccess.type]: endSpinnerFunction,
        [DiaperModalActions.diaperFailure.type]: endSpinnerFunction,
        [Actions.checkInSuccess.type]: endSpinnerFunction,
        [Actions.checkInFailed.type]: endSpinnerFunction,
        [Actions.checkOutSuccess.type]: endSpinnerFunction,
        [Actions.checkOutFailed.type]: endSpinnerFunction,
    },
    new State(),
);
